'use client';

import {
  Button,
  Card,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@routly/ui';
import { formatDistanceToNow } from 'date-fns';
import { Building2, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
} from '@/components/shared';
import { useDeactivateUser, useRevokeSessions, useUpdateUser } from '@/hooks/useUserActions';
import { useUsers } from '@/hooks/useUsers';
import { type User as AdminUser, USER_ROLES, type UserRole } from '@/lib/types';
import { EditUserDialog } from './EditUserDialog';
import { InviteUserDialog } from './InviteUserDialog';

/**
 * `MANAGER` was missing here while the backend already treated it as a
 * first-class role, so a real manager rendered with no label at all.
 */
const roleLabels: Record<UserRole, string> = {
  USER: 'User',
  MANAGER: 'Manager',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin',
};

export function UsersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useUsers({
    page,
    limit: 20,
    search: search || undefined,
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState title="Error loading users" message={error.message} />;
  }

  const users = data?.data || [];
  const pagination = data?.pagination;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="Users"
        description="Manage users across all organizations"
        actions={<InviteUserDialog />}
      />

      <div className="flex gap-4">
        <SearchInput placeholder="Search users..." value={search} onChange={handleSearch} />
      </div>

      {users.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No users yet"
          description={
            search
              ? 'No users match your search criteria.'
              : 'Users will appear here once they sign up through organizations.'
          }
        />
      ) : (
        <Card>
          <UsersTable users={users} />
          {pagination && (
            <Pagination
              pagination={pagination}
              currentPage={page}
              onPageChange={setPage}
              itemName="users"
            />
          )}
        </Card>
      )}
    </>
  );
}

function UsersTable({ users }: { users: AdminUser[] }) {
  const updateUser = useUpdateUser();
  const deactivateUser = useDeactivateUser();
  const revokeSessions = useRevokeSessions();

  const fail = (caught: unknown) =>
    toast.error('That did not work', {
      description: caught instanceof Error ? caught.message : undefined,
    });

  const changeRole = async (user: AdminUser, role: UserRole) => {
    if (role === user.role) return;
    try {
      await updateUser.mutateAsync({ id: user.id, role });
      toast.success(`${user.email} is now ${roleLabels[role]}`);
    } catch (caught) {
      fail(caught);
    }
  };

  /**
   * Deactivating closes every session in the same transaction, so it is not
   * undone by whatever token the user is holding. Reactivating is an ordinary
   * update.
   */
  const setActive = async (user: AdminUser, isActive: boolean) => {
    try {
      if (isActive) {
        await updateUser.mutateAsync({ id: user.id, isActive: true });
        toast.success(`${user.email} is active again`);
      } else {
        await deactivateUser.mutateAsync(user.id);
        toast.success(`${user.email} deactivated, sessions closed`);
      }
    } catch (caught) {
      fail(caught);
    }
  };

  const signOutEverywhere = async (user: AdminUser) => {
    try {
      const { revoked } = await revokeSessions.mutateAsync(user.id);
      toast.success(`Closed ${revoked} session(s) for ${user.email}`);
    } catch (caught) {
      fail(caught);
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>User</TableHead>
          <TableHead>Organization</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Last sign-in</TableHead>
          <TableHead>Sessions</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => (
          <TableRow key={user.id}>
            <TableCell>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{user.name || 'No name'}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
            </TableCell>

            <TableCell>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{user.organization.name}</span>
              </div>
            </TableCell>

            <TableCell>
              <Select
                value={user.role}
                onValueChange={(role) => changeRole(user, role as UserRole)}
              >
                <SelectTrigger className="h-8 w-40" aria-label="Role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {roleLabels[role]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TableCell>

            <TableCell className="text-muted-foreground text-sm">
              {/*
                An account nobody has ever used and one whose owner left six
                months ago look identical without this column.
              */}
              {user.lastSignInAt
                ? formatDistanceToNow(new Date(user.lastSignInAt), { addSuffix: true })
                : 'never'}
            </TableCell>

            <TableCell className="tabular-nums text-muted-foreground">
              {user.activeSessions ?? 0}
            </TableCell>

            <TableCell className="text-right space-x-2 whitespace-nowrap">
              <EditUserDialog user={user} />
              <Button
                size="sm"
                variant="outline"
                disabled={!user.activeSessions}
                onClick={() => signOutEverywhere(user)}
              >
                Sign out
              </Button>
              <Button
                size="sm"
                variant={user.isActive === false ? 'outline' : 'destructive'}
                onClick={() => setActive(user, user.isActive === false)}
              >
                {user.isActive === false ? 'Reactivate' : 'Deactivate'}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
